<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_monitoring_title')"
        :subtitle="t('vue.dashboard_overview_monitoring_subtitle')"
    >
        <div class="grid">
            <div
                v-for="row in rows"
                :key="row.code"
                class="flex min-h-[52px] items-center justify-between gap-3 border-t border-gray-200 py-2 first:border-t-0 first:pt-0"
            >
                <div class="min-w-0">
                    <p
                        class="text-[13px] font-medium leading-[18px] text-gray-900"
                    >
                        {{ row.name }}
                    </p>
                    <p class="mt-0.5 text-[13px] leading-[18px] text-gray-500">
                        {{ row.description }}
                    </p>
                </div>
                <DashboardStatusBadge
                    :status="row.status"
                    :label="row.statusLabel"
                />
            </div>
        </div>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import DashboardStatusBadge from "./DashboardStatusBadge.vue";
import type {
    IMonitoringServiceState,
    MonitoringStatus,
} from "@/types/Dashboard/IDashboardOverview";

interface MonitoringRow {
    code: string;
    name: string;
    description: string;
    status: MonitoringStatus;
    statusLabel: string;
}

const props = defineProps<{
    services: IMonitoringServiceState[];
    blacklist: IMonitoringServiceState | null;
    blacklistLoading: boolean;
}>();

const { t } = useTranslations();

const resolveBlacklistDescription = (
    service: IMonitoringServiceState,
): string => {
    const issues = service.count ?? 0;
    const total = service.total ?? 0;

    if (issues === 0) {
        return t(
            "vue.dashboard_overview_monitoring_blacklist_monitor_desc_clean",
            { total },
        );
    }

    return t(
        `vue.dashboard_overview_monitoring_blacklist_monitor_desc_${issues === 1 ? "one" : "other"}`,
        { count: issues, total },
    );
};

const resolveDescription = (service: IMonitoringServiceState): string => {
    if (service.code === "dmarc_reporting") {
        return t("vue.dashboard_overview_monitoring_dmarc_reporting_desc", {
            count: service.count ?? 0,
            total: service.total ?? 0,
        });
    }

    if (service.code === "blacklist_monitor") {
        return resolveBlacklistDescription(service);
    }

    const state = service.status === "enabled" ? "enabled" : "disabled";

    return t(`vue.dashboard_overview_monitoring_${service.code}_desc_${state}`);
};

const buildRow = (service: IMonitoringServiceState): MonitoringRow => ({
    code: service.code,
    name: t(`vue.dashboard_overview_monitoring_${service.code}`),
    description: resolveDescription(service),
    status: service.status,
    statusLabel: t(`vue.dashboard_overview_status_${service.status}`),
});

const buildBlacklistPlaceholder = (): MonitoringRow => ({
    code: "blacklist_monitor",
    name: t("vue.dashboard_overview_monitoring_blacklist_monitor"),
    description:
        props.blacklistLoading === true
            ? t("vue.dashboard_overview_loading")
            : t("vue.dashboard_overview_unavailable"),
    status: "disabled",
    statusLabel: t("vue.dashboard_overview_status_unknown"),
});

const rows = computed<MonitoringRow[]>(() => {
    const blacklistRow =
        props.blacklist !== null
            ? buildRow(props.blacklist)
            : buildBlacklistPlaceholder();

    const [dmarcReporting, ...rest] = props.services.map(buildRow);

    return dmarcReporting !== undefined
        ? [dmarcReporting, blacklistRow, ...rest]
        : [blacklistRow, ...rest];
});
</script>
